import React, { useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap/esm/index";
import Helmet from "react-helmet";
import { helmetJsonLdProp } from "react-schemaorg";
import { pushOutboundClick } from "../../assets/datalayerFunctions";

// Import Components
import ITPHeroHeader from "../../components/HeroHeader/ITPHeroHeader";
import Accordion from "../../components/shared-ui/Accordion/Accordion";
import ItpNavigation from "../../components/ITPNavigation/ITPNavigation";
import FooterITP from "../../components/shared-ui/Footer/Footer-ITP";
import BackToTopBtn from "../../components/shared-ui/BackToTopBtn/BackToTopBtn";
import ISIITPinline from "../../components/shared-ui/ISI/ISIITP-inline";
import ISIwrapper from "../../components/shared-ui/ISI/ISI-wrapper";
import ISIITP from "../../components/shared-ui/ISI/ISIITP";
import LookingForSupport from "../../components/shared-ui/Cards/LookingForSupport";

// Import Styles
import "../../css/TreatingITP.scss";
import PrimaryButton from "../../components/shared-ui/Buttons/PrimaryButton";

function TreatingITP() {
  useEffect(() => {
    window.scrollTo(0, 0);
    let location = window.location.pathname;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      page: {
        path: `${location}`,
        title: "Diagnosis & Treatment | DOPTELET® (avatrombopag)",
        environment: "dev",
        indication: "ITP",
        audience: "DTC",
      },
    });
  }, []);

  const showPhreesiaModal = (event) => {
    let modal = document.querySelector("#phreesia-modal");
    document.body.classList.add("no-scroll");
    modal.classList.remove("fadeOut");
    modal.style.display = "block";
    modal.classList.add("fadeIn");
    modal.style.height = "100%";
    modal.querySelector('.exitBtn').addEventListener(
      "click",
      function() {
        modal.style.display = "none";
        document.body.classList.remove("no-scroll");
      }
    );
    modal.querySelector('.modal-overlay').addEventListener(
      "click",
      function() {
        modal.style.display = "none";
        document.body.classList.remove("no-scroll");
      }
    );
  };

  // Simulate clicking..this is dirt, im sorry!
  // async function simulateMouseClick(el) {
  //   let opts = { view: window, bubbles: true, cancelable: true, buttons: 1 };
  //   el.dispatchEvent(new MouseEvent("mousedown", opts));
  //   await new Promise((r) => setTimeout(r, 50));
  //   el.dispatchEvent(new MouseEvent("mouseup", opts));
  //   el.dispatchEvent(new MouseEvent("click", opts));
  // }

  // const whatTPORA = (event) => {
  //   const tpora = document
  //     .getElementById("what-tpo-ra")
  //     .getElementsByClassName("btn-link");
  //   simulateMouseClick(tpora[0]);
  // };

  // const howITP = (event) => {
  //   const tpora = document
  //     .getElementById("how-itp-diagnosed")
  //     .getElementsByClassName("btn-link");
  //   simulateMouseClick(tpora[0]);
  // };

  return (
    <div className="main_content_wrap">
      <Helmet
        script={[
          helmetJsonLdProp({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "MedicalWebPage",
                url: "https://doptelet.com/what-is-itp/itp-diagnosis-and-treatment/",
                name: "Immune Thrombocytopenia (ITP) Diagnosis & Treatment | DOPTELET® (avatrombopag)",
                mainEntity: {
                  "@type": "MedicalCondition",
                  name: "chronic immune thrombocytopenia",
                  alternateName: "ITP",
                  possibleTreatment: [
                    { "@type": "DrugClass", name: "TPO-RA" },
                    { "@type": "DrugClass", name: "corticosteroids" },
                    { "@type": "DrugClass", name: "IVIG" },
                    { "@type": "DrugClass", name: "rituximab" },
                  ],
                },
                mainContentOfPage: [
                  "TREATING CHRONIC ITP",
                  "People living with chronic ITP have several treatment options to consider. Learn about those options below and discover more about Doptelet, a type of medicine known as a TPO-RA",
                  "Doptelet is a TPO-RA, not an immunosuppressant. TPO-RAs are a type of medication that mimics your body's natural process of creating platelets. Doptelet can help boost the pathway responsible for making platelets, which adds to the platelets your body is already making. This ultimately increases your overall count. Doptelet doesn't require weekly doctor's appointments for treatment injections or become less effective when you eat certain foods, like ice cream. You can also take Doptelet no matter where you are—just be sure to take it with food and at the same time each day. Work with your doctor to determine a dosing schedule that works best for you. When you start Doptelet, your doctor will monitor your platelets weekly and then monthly as soon as you reach a platelet count of 50,000 per microliter or more.",
                ],
                lastReviewed: "2025-09-01",
                reviewedBy: {
                  "@type": "Organization",
                  name: "Sobi",
                  url: "https://www.sobi.com/usa/",
                },
              },
            ],
          }),
        ]}
      >
        <title>
          Immune Thrombocytopenia (ITP) Diagnosis & Treatment | DOPTELET®
          (avatrombopag)
        </title>
        <meta
          name="description"
          content="Learn about how ITP is diagnosed, what your doctor may look for, platelet count ranges, and various treatment options like splenectomy, TPO-RAs, and more."
        />
        <link
          rel="canonical"
          href="https://doptelet.com/what-is-itp/itp-diagnosis-and-treatment/"
        />
      </Helmet>
      <ItpNavigation />
      <div className="inner-content-wrap">
        <ITPHeroHeader
          extraClass="large-hero inner-bg"
          markup={
            <>
              <h1>TREATING CHRONIC ITP</h1>
              <p className="mb-0">
                People living with chronic ITP have several treatment options to consider. Learn about those options below and discover more about Doptelet, a type of medicine known as a TPO-RA.
              </p>
            </>
          }
          maxWidth="515"
          bgImage={["treating-itp-hero.jpg", "treating-itp-hero@2x.jpg"]}
          bgImageMobile={[
            "treating-itp-hero-mobile.jpg",
            "treating-itp-hero-mobile@2x.jpg",
          ]}
          imageAlt="Doptelet character taking photo of standing face cutout in carnival scenery"
        />

        <section className="section-padding">
          <Container>
            <Row className="align-items-center">
              <Col lg={{ span: 12 }}>
                <h2>How is chronic ITP treated in adults?</h2>
                <p>
                  Not all treatments are created equal. There are a few options for treating chronic ITP, such as corticosteroids, intravenous immunoglobulin (IVIG), rituximab, and TPO-RAs—like Doptelet—that offer different benefits. Click on each of these common treatment options to learn how they work.
                </p>
                <Accordion
                  title="Corticosteroids"
                  id="corticosteroids"
                  className="scroll-to-section"
                  markup={
                    <>
                      <p>Corticosteroids are immunosuppressants that work by reducing inflammation and the overall activity of your immune system. For ITP, they are commonly prescribed right after diagnosis and are intended for short-term use to stop your body from destroying platelets.</p>
                      <p>While steroids can be highly effective, continued use, especially at high doses, may have side effects such as increased weight gain, swelling of the face, sleep problems, and mood disturbances such as irritability. Steroids also come with increased risk of infections, diabetes, cataracts, liver issues, high blood pressure, high blood sugar, osteoporosis, and bone and muscle disorders.</p>
                      <p className="text-blue">For these reasons, the trusted experts who wrote the current American Society of Hematology (ASH) guidelines recommend limiting steroid treatment to less than 6 weeks.</p>
                      <p className="text-blue">Ask your doctor for more information about the ASH Guidelines</p>
                    </>
                  }
                />
                <Accordion
                  title="Intravenous Immunoglobulin (IVIG)"
                  id="intravenous"
                  bodyText="IVIG is an immune-modulating therapy made of antibodies that can help regulate your immune system. IVIG is a first-line treatment typically used in emergency, short-term situations when platelet counts are severely low. IVIG works by reducing certain immune responses that may be responsible for destroying platelets, but unlike immunosuppressants, it does not weaken the entire immune system."
                  className="scroll-to-section"
                />
                <Accordion
                  title="Rituximab"
                  bodyText="Rituximab is an immunosuppressant. Like steroids, it works to reduce the overall activity of your immune system to stop your body from destroying platelets. Because rituximab changes how your immune system works, it may be easier for you to get sick or develop other conditions."
                  className="scroll-to-section mb-40"
                />
              </Col>
            </Row>
          </Container>
          <Container>
            <Row>
              <Col md={{ span: 12 }}>
                <div className="contained navy-bg d-flex align-items-center flex-column flex-lg-row">
                  <div className="col-lg-8 offset-lg-2 p-0">
                    <p className="mb-0 text-white text-center">
                      If immunosuppressants aren’t working for you, and your platelet counts don’t stay lifted, your doctor may consider a thrombopoietin receptor agonist (TPO-RA), such as Doptelet.
                    </p>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="cloud-blue-bg section-padding">
          <Container>
            <Row className="align-items-center">
              <Col lg={{ span: 12 }}>
                <h2>Treating with Doptelet, a medicine called a TPO-RA</h2>
                <p>Doptelet is a TPO-RA, not an immunosuppressant. TPO-RAs are a type of medication that mimics your body’s natural process of creating platelets. Doptelet can help boost the pathway responsible for making platelets, which adds to the platelets your body is already making. This ultimately increases your overall count.</p>
                <p>Doptelet doesn’t require weekly doctor’s appointments for treatment injections or become less effective when you eat certain foods, like ice cream. You can also take Doptelet no matter where you are—just be sure to take it with food and at the same time each day. Work with your doctor to determine a dosing schedule that works best for you.</p>
                <p className="mb-0">When you start Doptelet, your doctor will monitor your platelets weekly and then monthly as soon as you reach a platelet count of 50,000 per microliter or more.</p>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="section-padding">
          <Container>
            <Row>
              <Col md={{ span: 12 }}>
                <div className="contained orange-bg d-flex align-items-center flex-column">
                    <h2>Find a chronic ITP specialist near you</h2>
                    <p className="mb-0">
                      One of the most important steps to getting the care you deserve is finding the right hematologist in your area.
                    </p>
                    <PrimaryButton
                      title="FIND A SPECIALIST"
                      addClass="btn btn-primary mb-4 mt-4"
                      onClick={showPhreesiaModal}
                    />
                    <p className="mb-0">Sobi is not responsible for the choices or actions that you or others take because of information provided by this free tool.</p>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <LookingForSupport addClass="section-padding pt-0" />

        <ISIITPinline />
      </div>
      <FooterITP />
      <ISIwrapper>
        <ISIITP />
      </ISIwrapper>
      <BackToTopBtn />
    </div>
  );
}

export default TreatingITP;
